import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Fade } from "@material-ui/core";
import { Toast } from "primereact/toast";
import Input from "../Form/Input";
import { Calendar } from "primereact/calendar";
import { useForm, Controller } from "react-hook-form";
import api from "../../services/api";
import { Dropdown } from "primereact/dropdown";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "../../contexts/AuthContext";
import {
  batteryToFormState,
  emptyBatteryFormState,
  formatDensity
} from "../../utils/batteryUtils";

const schema = z.object({
  error_description: z.string().nonempty({ message: "Digite a descrição." }),
  hour_meter: z.string().nonempty({ message: "Digite o horimetro." }),
  mileage: z.any(),
  cause: z.string().nonempty({ message: "Digite a causa." }),
  solution: z.string().nonempty({ message: "Digite a solução do problema." }),
  has_pendency: z
    .boolean()
    .refine((value) => value === true || value === false, {
      message: "Selecione se houve pendência ou não."
    }),
  pendency: z.string().nullable(),
  testeEfetuado: z
    .boolean()
    .refine((value) => value === true || value === false, {
      message: "Selecione se o teste foi efetuado ou não."
    }),
  misuse: z.boolean().refine((value) => value === true || value === false, {
    message: "Selecione se teve mau uso ou não."
  }),
  resultadoPositivo: z
    .boolean()
    .refine((value) => value === true || value === false, {
      message: "Selecione se o resultado foi positivo ou não."
    }),
  aplicacaoDePecas: z
    .boolean()
    .refine((value) => value === true || value === false, {
      message: "Selecione se o resultado foi positivo ou não."
    }),
  costumer_name: z.string().nullable(),
  costumer_email: z.string().email().optional().or(z.literal("")),
  date: z.date({
    required_error: "Digite a data.",
    invalid_type_error: "Digite a data."
  }),
  start_attendance: z.date({
    required_error: "Digite o horário de início.",
    invalid_type_error: "Digite o horário de início."
  }),
  end_attendance: z.date({
    required_error: "Digite o horário final.",
    invalid_type_error: "Digite o horário final."
  }),
  service_call: z.object({
    id: z.any()
  }),
  service_call_id: z.any(),
  is_active: z.any(),
  is_guarantee: z.any(),
  attendance: z.array(z.string()),
  displacement_time: z.string(),
  attendance_time: z.string(),
  created_by: z.number().min(1, "Selecione um técnico."),
  internal_notes: z
    .string()
    .max(10000, { message: "Máximo de 10000 caracteres." })
    .optional()
});

const initialBatteryElements = Array.from({ length: 24 }, () => ({
  voltage: "",
  density: ""
}));

export default function CreateCorrectiveForm({
  visible = true,
  onHide = () => {},
  currentCorrective,
  toast,
  setLoading,
  getCorretivas
}) {
  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: false
  });

  const [empresas, setEmpresas] = useState([{ value: "", label: "" }]);
  const [techs, setTechs] = useState();
  const [firstTechnician, setFirstTechnician] = useState();
  const [secondTechnician, setSecondTechnician] = useState();
  const [serviceCalls, setServiceCalls] = useState([{ value: "", label: "" }]);
  const [companyByServiceCall, setCompanyByServiceCall] = useState("");
  const [equipmentByServiceCall, setEquipmentByServiceCall] = useState("");
  const [serviceCallState, setServiceCallState] = useState();

  const [batteryBrand, setBatteryBrand] = useState("");
  const [batteryManufactureDate, setBatteryManufactureDate] = useState("");
  const [batteryReadingDate, setBatteryReadingDate] = useState("");
  const [batteryTotalVoltage, setBatteryTotalVoltage] = useState("");
  const [batteryElements, setBatteryElements] = useState(
    initialBatteryElements
  );
  const [batteryErrors, setBatteryErrors] = useState({});
  const [batteries, setBatteries] = useState([emptyBatteryFormState()]);

  const watchEmpresa = watch("company_id");
  const watchDate = watch("date");
  const watchCreated_by = watch("created_by");
  const watchHasPendency = watch("has_pendency");
  const watchInternalNotes = watch("internal_notes", "");
  const { access_type } = useAuth();

  useEffect(() => {
    if (access_type === "Admin") {
      api.get("service_calls/allServiceCalls").then((response) => {
        setServiceCalls(response.data);
      });
      api.get("/users/getTechs").then((response) => {
        setTechs(response.data);
      });
    }
  }, []);

  const formatDate = (dataString) => {
    const date = new Date(`${dataString} 10:00`);
    return date;
  };

  useEffect(() => {
    if (currentCorrective) {
      setValue("date", formatDate(currentCorrective?.date));
      setValue("error_description", currentCorrective?.error_description);
      setValue("hour_meter", currentCorrective?.hour_meter);
      const mileage = parseInt(currentCorrective?.mileage);
      setValue("mileage", isNaN(mileage) ? 0 : currentCorrective?.mileage);
      setValue("cause", currentCorrective?.cause);
      setValue("solution", currentCorrective?.solution);
      setValue("has_pendency", Boolean(currentCorrective?.has_pendency));
      setValue("pendency", currentCorrective?.pendency);
      setValue("testeEfetuado", Boolean(currentCorrective?.testeEfetuado));
      setValue("misuse", Boolean(currentCorrective?.misuse));
      setValue(
        "resultadoPositivo",
        Boolean(currentCorrective?.resultadoPositivo)
      );
      setValue(
        "aplicacaoDePecas",
        Boolean(currentCorrective?.aplicacaoDePecas)
      );
      setValue(
        "costumer_name",
        currentCorrective?.costumer_name ||
          currentCorrective?.company?.contact_name ||
          ""
      );
      setValue("costumer_email", currentCorrective?.costumer_email || "");
      if (
        currentCorrective?.attendance &&
        currentCorrective?.attendance !== "00:00"
      ) {
        setValue(
          "start_attendance",
          new Date(
            `1970-01-01T${currentCorrective?.attendance
              .split("-")[0]
              ?.trim()}:00`
          )
        );
        setValue(
          "end_attendance",
          new Date(
            `1970-01-01T${currentCorrective?.attendance
              .split("-")[1]
              ?.trim()}:00`
          )
        );
      } else {
        setValue("start_attendance", new Date(`1970-01-01T00:00:00`));
        setValue("end_attendance", new Date(`1970-01-01T00:00:00`));
      }
      setValue("company", currentCorrective?.company);
      setValue("service_call", currentCorrective?.service_call);
      setValue("service_call_id", currentCorrective?.service_call?.id);
      setValue("attendance", []);
      setValue("attendance_time", "");
      setValue("displacement_time", "");
      setValue("created_by", currentCorrective?.created_by?.id);
      setValue("is_active", currentCorrective?.is_active);
      setValue("is_guarantee", currentCorrective?.is_guarantee);
      setServiceCallState(currentCorrective?.service_call);
      setCompanyByServiceCall(currentCorrective?.company);
      setEquipmentByServiceCall(currentCorrective?.service_call?.equipment);
      setFirstTechnician(currentCorrective?.creator?.name || "");
      setBatteryBrand(currentCorrective?.brand || "");
      setBatteryManufactureDate(currentCorrective?.manufacture_date || "");
      setBatteryReadingDate(currentCorrective?.reading_date || "");
      setBatteryTotalVoltage(currentCorrective?.total_voltage || "");
      setBatteryElements(
        Array.from({ length: 24 }, (_, idx) => ({
          voltage: currentCorrective[`element_${idx + 1}_voltage`] || "",
          density: currentCorrective[`element_${idx + 1}_density`] || ""
        }))
      );
      const batteriesFromApi = currentCorrective?.batteries;
      if (batteriesFromApi?.length > 0) {
        setBatteries(batteriesFromApi.map((b) => batteryToFormState(b)));
      } else if (
        currentCorrective?.brand ||
        currentCorrective?.pat ||
        currentCorrective?.manufacture_date
      ) {
        setBatteries([batteryToFormState(currentCorrective)]);
      } else {
        setBatteries([emptyBatteryFormState()]);
      }
      setValue("internal_notes", currentCorrective?.internal_notes || "");
    } else {
      setValue("error_description", "");
      setValue("company_id", "");
      setValue("equipment_id", "");
      setValue("hour_meter", "");
      setValue("mileage", "");
      setValue("cause", "");
      setValue("solution", "");
      setValue("has_pendency", "");
      setValue("pendency", "");
      setValue("testeEfetuado", "");
      setValue("misuse", "");
      setValue("resultadoPositivo", "");
      setValue("aplicacaoDePecas", "");
      setValue("costumer_name", "");
      setValue("costumer_email", "");
      setValue("start_attendance", "");
      setValue("end_attendance", "");
      setValue("attendance", []);
      setValue("attendance_time", "");
      setValue("displacement_time", "");
      setValue("company", "");
      setValue("service_call", { id: "" });
      setValue("service_call_id", "");
      setValue("date", formatDate(getTodayDate()));
      setValue("created_by", "");
      setValue("is_active", "");
      setValue("is_guarantee", "");
      setServiceCallState(null);
      setCompanyByServiceCall("");
      setEquipmentByServiceCall("");
      setBatteryBrand("");
      setBatteryManufactureDate("");
      setBatteryReadingDate("");
      setBatteryTotalVoltage("");
      setBatteryElements(initialBatteryElements);
      setBatteries([emptyBatteryFormState()]);
      setValue("internal_notes", "");
    }
  }, [currentCorrective]);

  const validateBattery = () => {
    // Só valida se NÃO estiver editando
    if (currentCorrective) {
      setBatteryErrors({});
      return true;
    }
    const errors = {};
    if (!batteryBrand) errors.brand = "Marca obrigatória";
    if (!batteryManufactureDate)
      errors.manufactureDate = "Data de fabricação obrigatória";
    if (!batteryReadingDate) errors.readingDate = "Data da medição obrigatória";
    if (!batteryTotalVoltage) errors.totalVoltage = "Tensão total obrigatória";
    setBatteryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitForm = async (data) => {
    setLoading(true);
    hideForm();
    try {
      if (isNaN(data.mileage)) {
        data.mileage = 0;
      } else {
        data.mileage = parseInt(data.mileage);
      }
      data.date = data.date.toISOString().split("T")[0];
      data.hour_meter = parseInt(data.hour_meter);
      const attendanceArray = getAttendance(
        data.start_attendance,
        data.end_attendance
      );
      data.attendance = attendanceArray;
      data.attendance_time = getDifferenceAttendanceTime(
        attendanceArray.join("-")
      );
      data.company_id = companyByServiceCall?.id;

      data.second_tech = secondTechnician?.id;
      data.pendency = data.pendency ?? currentCorrective?.pendency;

      if (!validateBattery()) {
        setLoading(false);
        if (!currentCorrective) {
          toast.current.show({
            severity: "error",
            summary: "Erro",
            detail:
              "Preencha corretamente os campos obrigatórios da medição de bateria.",
            life: 3000
          });
        }
        return;
      }

      if (currentCorrective) {
        data.batteries = batteries.map((bat) => {
          const item = {
            brand: bat.brand,
            pat: bat.brand,
            manufacture_date: bat.manufactureDate,
            reading_date: bat.readingDate,
            total_voltage: bat.totalVoltage,
            voltagem_nominal: bat.elementCount === 12 ? 24 : 48
          };
          bat.elements.slice(0, bat.elementCount).forEach((el, idx) => {
            item[`element_${idx + 1}_voltage`] = el.voltage;
            item[`element_${idx + 1}_density`] = el.density;
          });
          return item;
        });
      } else {
        data.brand = batteryBrand;
        data.manufacture_date = batteryManufactureDate;
        data.reading_date = batteryReadingDate;
        data.total_voltage = batteryTotalVoltage;
        batteryElements.forEach((el, idx) => {
          data[`element_${idx + 1}_voltage`] = el.voltage;
          data[`element_${idx + 1}_density`] = el.density;
        });
      }

      if (currentCorrective) {
        data.equipment_id = currentCorrective?.service_call?.equipment_id;
        data.service_call_id = data?.service_call?.id;
        data.created_by = currentCorrective?.created_by?.id;
        await api.put(
          `/corrective/update-corrective/${currentCorrective.id}`,
          data
        );
      } else {
        data.created_by = firstTechnician?.id;
        data.service_call_id = serviceCallState?.id;
        data.service_call = serviceCallState;
        data.is_active = companyByServiceCall?.is_active;
        data.is_guarantee = companyByServiceCall?.is_guarantee;
        data.equipment_id = serviceCallState?.equipment_id;
        await api.post(`/corrective/create-corrective-web`, data);
        setServiceCallState("");
        setCompanyByServiceCall("");
        setEquipmentByServiceCall("");
        setFirstTechnician("");
        setSecondTechnician("");
      }
      toast.current.show({
        severity: "success",
        summary: "",
        detail: `Corretiva enviada com sucesso!`,
        life: 3000
      });
      setLoading(false);
      getCorretivas();
    } catch (error) {
      toast.current.show({
        severity: "err",
        summary: "",
        detail: `Erro ao enviar corretiva!`,
        life: 3000
      });
      setLoading(false);
    }
  };
  const getDifferenceAttendanceTime = (attendance) => {
    const [start_time, end_time] = attendance.split("-");
    const [start_hours, start_minutes] = start_time.split(":").map(Number);
    const [end_hours, end_minutes] = end_time.split(":").map(Number);

    let differenceInMinutes =
      end_hours * 60 + end_minutes - (start_hours * 60 + start_minutes);

    if (differenceInMinutes < 0) {
      differenceInMinutes += 24 * 60;
    }

    const differenceHours = Math.floor(differenceInMinutes / 60);
    const differenceMinutes = differenceInMinutes % 60;

    const formattedDifference = `${String(differenceHours).padStart(
      2,
      "0"
    )}:${String(differenceMinutes).padStart(2, "0")}`;

    return formattedDifference;
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    let month = today.getMonth() + 1;
    let day = today.getDate();
    if (month < 10) {
      month = `0${month}`;
    }
    if (day < 10) {
      day = `0${day}`;
    }
    return `${year}-${month}-${day}`;
  };

  const getAttendance = (start_time, end_time) => {
    const startTime = new Date(start_time);
    const endTime = new Date(end_time);

    let startH = startTime.getUTCHours() - 3;
    let endH = endTime.getUTCHours() - 3;
    const startMin = startTime.getUTCMinutes().toString()?.padStart(2, "0");
    const endMin = endTime.getUTCMinutes().toString()?.padStart(2, "0");
    if (startH < 0) {
      startH += 24;
    }
    if (endH < 0) {
      endH += 24;
    }
    const formattedStartH = startH.toString()?.padStart(2, "0");
    const formattedEndH = endH.toString()?.padStart(2, "0");
    return [`${formattedStartH}:${startMin}`, `${formattedEndH}:${endMin}`];
  };

  const handleChangeSelect = (selectedOption) => {
    const company = empresas.find(
      (empresa) => empresa.id === selectedOption.value
    );
    if (company) {
      return company;
    }
  };
  const handleChangeSelectServiceCall = (selectedOption) => {
    const serviceCall = serviceCalls.find(
      (service) => service.id === selectedOption.value
    );
    if (serviceCall) {
      const updatedServiceCallState = serviceCall;
      setValue("costumer_name", updatedServiceCallState?.company?.contact_name);
      setValue("costumer_email", updatedServiceCallState?.company?.email);
      setServiceCallState(updatedServiceCallState);
      setCompanyByServiceCall(updatedServiceCallState?.company);
      setEquipmentByServiceCall(updatedServiceCallState?.equipment);
      return serviceCall;
    }
  };

  const handleChangeSelectTechnician = (selectedOption) => {
    const technician = techs.find((tech) => tech.id === selectedOption.value);
    if (technician) {
      const selectedTechnician = technician;
      setFirstTechnician(selectedTechnician);
      const filteredTechs = techs.filter(
        (tech) => tech.name !== selectedTechnician.name
      );
      setTechs(filteredTechs);
      return technician;
    }
  };
  const handleChangeSelectSecondTechnician = (selectedOption) => {
    const technician = techs.find((tech) => tech.id === selectedOption.value);
    if (technician) {
      if (technician === firstTechnician) {
        return;
      }
      const selectedTechnician = technician;
      setSecondTechnician(selectedTechnician);
      return technician;
    }
  };

  const handleChangeSelectEquipment = (selectedOption) => {
    const equipment = watchEmpresa?.equipments.find(
      (equipment) => equipment.id == selectedOption.value
    );
    if (equipment) {
      return equipment;
    }
  };

  const hideForm = () => {
    setServiceCallState("");
    setCompanyByServiceCall("");
    setEquipmentByServiceCall("");
    setFirstTechnician("");
    setSecondTechnician("");
    reset();
    onHide();
  };

  const footer = (
    <React.Fragment>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          hideForm();
        }}
      />
      <Button
        label={currentCorrective ? "Editar" : "Criar"}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit((data) => submitForm(data))}
      />
    </React.Fragment>
  );
  const renderServiceNumber = (rowData) => {
    const year = rowData?.service_date?.split("-")[0];
    return year + "." + rowData?.service_number;
  };
  const renderDisplayServiceCall = (serviceCall) => {
    const year = serviceCall?.service_date?.split("-")[0];
    return year + "." + serviceCall?.service_number;
  };

  const createForm = () => {
    return (
      <div className="container pt-2">
        <Fade in={true}>
          <div className="row">
            <form
              className={
                "d-flex flex-column col-md-12 shadow rounded p-4 justify-content-center align-content-center"
              }
            >
              <div className={"row pb-1"}>
                <div className={"col-md-12 pb-2 d-flex flex-column"}>
                  <label htmlFor="Service call">Chamado: </label>

                  <Controller
                    control={control}
                    name="service_call_id"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        disabled={Boolean(currentCorrective)}
                        placeholder={
                          currentCorrective
                            ? renderDisplayServiceCall(
                                currentCorrective?.service_call
                              )
                            : "Selecione o número do chamado"
                        }
                        options={serviceCalls.map((serviceCall) => ({
                          ...serviceCall,
                          renderNumber: renderServiceNumber(serviceCall)
                        }))}
                        valueTemplate={
                          serviceCallState
                            ? renderServiceNumber(serviceCallState)
                            : ""
                        }
                        labelTemplate={
                          renderServiceNumber(serviceCallState) || ""
                        }
                        optionLabel="renderNumber"
                        optionValue="id"
                        onChange={(event) => {
                          const serviceCall =
                            handleChangeSelectServiceCall(event);
                          onChange(serviceCall);
                        }}
                        label={""}
                        filter
                        error={errors.company?.message}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={"row pb-1"}>
                <div className={"col-md-12 pb-2 d-flex flex-column"}>
                  <label htmlFor="Empresa">Empresa: </label>
                  <Controller
                    control={control}
                    name="company_id"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        value={companyByServiceCall}
                        optionLabel={"name"}
                        optionValue={"id"}
                        disabled
                        placeholder={companyByServiceCall?.name || ""}
                        label={"Empresa"}
                        filter
                        error={errors.company?.message}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>
              <div className={"row pb-1"}>
                <div className={"col-md-12 pb-2 d-flex flex-column"}>
                  <label htmlFor="Equipamento">Equipamento: </label>
                  <Controller
                    control={control}
                    name="equipment_id"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        value={equipmentByServiceCall}
                        disabled
                        placeholder={equipmentByServiceCall?.equipment || ""}
                        onChange={(event) => {
                          const equipment = handleChangeSelectEquipment(event);
                          onChange(equipment);
                        }}
                        optionLabel={"equipment"}
                        optionValue={"id"}
                        filter
                        label={"Equipamento"}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={"d-flex justify-content-around"}>
                <div className={"col-md-5 pb-2 d-flex flex-column"}>
                  <label htmlFor="Técnico">Técnico: </label>
                  <Controller
                    control={control}
                    name="created_by"
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <Dropdown
                          value={
                            currentCorrective
                              ? currentCorrective?.created_by?.id
                              : firstTechnician?.id
                          }
                          disabled={Boolean(currentCorrective)}
                          placeholder={
                            firstTechnician ? firstTechnician?.name : "Técnico"
                          }
                          className={
                            errors.created_by ? "mb-2 border-danger" : "mb-2"
                          }
                          onChange={(event) => {
                            const technician =
                              handleChangeSelectTechnician(event);
                            onChange(technician?.id);
                          }}
                          options={techs}
                          optionLabel={"name"}
                          optionValue={"id"}
                          filter
                          label={"technician"}
                          {...field}
                        />
                      );
                    }}
                  />
                </div>
                <div className={"col-md-5 pb-2 d-flex flex-column"}>
                  <label htmlFor="Técnico">Técnico Secundario* : </label>
                  <Controller
                    control={control}
                    name="second_tech"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        value={field.value}
                        disabled={firstTechnician ? false : true}
                        placeholder={
                          secondTechnician
                            ? secondTechnician?.name
                            : "Técnico Secundário"
                        }
                        onChange={(event) => {
                          handleChangeSelectSecondTechnician(event);
                        }}
                        options={techs}
                        optionLabel={"name"}
                        optionValue={"id"}
                        filter
                        label={"technician"}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={"row pb-2"}>
                <div className={"col-md-12 d-flex flex-column"}>
                  <Input
                    error={errors.error_description?.message}
                    name={"error_description"}
                    label={"Descrição"}
                    register={register}
                  />
                </div>
              </div>
              <div className={"d-flex justify-content-around "}>
                <div className="col-md-4 pb-3 d-flex flex-column">
                  <div>
                    <Input
                      name={"mileage"}
                      error={errors.mileage?.message}
                      label={"Quilometragem"}
                      register={register}
                      type="number"
                    />
                  </div>
                </div>

                <div className={"col-md-4 pb-3 d-flex flex-column"}>
                  <div>
                    <Input
                      error={errors.hour_meter?.message}
                      name={"hour_meter"}
                      label={"Horimetro"}
                      register={register}
                      type="number"
                    />
                  </div>
                </div>
              </div>

              <div className={"d-flex justify-content-around "}>
                <div className="col-md-3 d-flex flex-column">
                  <label htmlFor="startTime">Data</label>
                  <Controller
                    control={control}
                    name="date"
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <Calendar
                          className={
                            errors.date?.message ? "p-invalid mb-2" : "mb-2"
                          }
                          value={field.value}
                          onChange={onChange}
                        />
                      );
                    }}
                  />
                  {errors.date?.message && (
                    <h6 className="text-danger font-weight-bold">
                      {errors.date?.message}
                    </h6>
                  )}
                </div>
                <div className="col-md-3 d-flex flex-column">
                  <label htmlFor="startTime">Hora de início</label>
                  <Controller
                    control={control}
                    name="start_attendance"
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <Calendar
                          className={
                            errors.start_attendance?.message
                              ? "p-invalid mb-2"
                              : "mb-2"
                          }
                          value={field.value}
                          onChange={onChange}
                          timeOnly
                        />
                      );
                    }}
                  />
                  {errors.start_attendance?.message && (
                    <h6 className="text-danger font-weight-bold">
                      {errors.start_attendance?.message}
                    </h6>
                  )}
                </div>
                <div className="col-md-3 d-flex flex-column">
                  <label htmlFor="startTime">Hora final</label>
                  <Controller
                    control={control}
                    name="end_attendance"
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <Calendar
                          className={
                            errors.end_attendance?.message
                              ? "p-invalid mb-2"
                              : "mb-2"
                          }
                          value={field?.value}
                          onChange={onChange}
                          timeOnly
                        />
                      );
                    }}
                  />
                  {errors.end_attendance?.message && (
                    <h6 className="text-danger font-weight-bold">
                      {errors.end_attendance?.message}
                    </h6>
                  )}
                </div>
              </div>

              <div className={"row pb-1"}>
                <div className={"col-md-12 d-flex flex-column"}>
                  <Input
                    name={"cause"}
                    error={errors.cause?.message}
                    label={"Causa"}
                    register={register}
                  />
                </div>
              </div>

              <div className={"row pb-1"}>
                <div className={"col-md-12 d-flex flex-column"}>
                  <Input
                    error={errors.solution?.message}
                    name={"solution"}
                    label={"Solução"}
                    register={register}
                  />
                </div>
              </div>

              {access_type !== "Costumer" && (
                <div className={"row pb-1"}>
                  <div className={"col-md-12 d-flex flex-column"}>
                    <Input
                      error={errors.internal_notes?.message}
                      name={"internal_notes"}
                      label={"Informações de uso interno"}
                      register={register}
                      asTextArea
                      rows={4}
                      maxLength={10000}
                    />
                    <div className="d-flex justify-content-end small text-muted">
                      {watchInternalNotes?.length || 0}/10000
                    </div>
                  </div>
                </div>
              )}

              <div className={"d-flex justify-content-around"}>
                <div className={"col-md-2 pb-3 d-flex flex-column"}>
                  <label htmlFor="Houve pendencia?">Houve pendência? </label>
                  <Controller
                    control={control}
                    name="has_pendency"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        className={
                          errors.has_pendency?.message
                            ? "mb-2 border-danger"
                            : "mb-2"
                        }
                        valueTemplate={field?.value ? "Sim" : "Não"}
                        optionLabel="label"
                        optionValue="value"
                        options={[
                          { label: "Sim", value: true },
                          { label: "Não", value: false }
                        ]}
                        value={field.value}
                        onChange={onChange}
                        {...field}
                      />
                    )}
                  />
                </div>
                {watchHasPendency && (
                  <div className={"col-md-6 pb-3 d-flex flex-column"}>
                    <Input
                      name={"pendency"}
                      label={"Pendência"}
                      register={register}
                    />
                  </div>
                )}
              </div>

              <div className={"d-flex justify-content-around"}>
                <div className={"col-md-2 pb-3 d-flex flex-column"}>
                  <label htmlFor="Teste efetuado?">Teste efetuado? </label>
                  <Controller
                    control={control}
                    name="testeEfetuado"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        className={
                          errors.testeEfetuado?.message
                            ? "mb-2 border-danger"
                            : "mb-2"
                        }
                        options={[
                          { label: "Sim", value: true },
                          { label: "Não", value: false }
                        ]}
                        value={field.value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className={"col-md-2 pb-3 d-flex flex-column"}>
                  <label htmlFor="Mau uso?">Mau uso? </label>
                  <Controller
                    control={control}
                    name="misuse"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        className={
                          errors.misuse?.message ? "mb-2 border-danger" : "mb-2"
                        }
                        options={[
                          { label: "Sim", value: true },
                          { label: "Não", value: false }
                        ]}
                        value={field.value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className={"col-md-2 pb-3 d-flex flex-column"}>
                  <label htmlFor="Resultado positivo?">
                    Resultado positivo?{" "}
                  </label>
                  <Controller
                    control={control}
                    name="resultadoPositivo"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        className={
                          errors.resultadoPositivo?.message
                            ? "mb-2 border-danger"
                            : "mb-2"
                        }
                        options={[
                          { label: "Sim", value: true },
                          { label: "Não", value: false }
                        ]}
                        value={field.value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className={"col-md-2 pb-3 d-flex flex-column"}>
                  <label htmlFor="Aplicação de peças?">
                    Aplicação de peças?
                  </label>
                  <Controller
                    control={control}
                    name="aplicacaoDePecas"
                    render={({ field: { onChange, ...field } }) => (
                      <Dropdown
                        className={
                          errors.aplicacaoDePecas?.message
                            ? "mb-2 border-danger"
                            : "mb-2"
                        }
                        valueTemplate={field?.value ? "Sim" : "Não"}
                        optionLabel="label"
                        optionValue="value"
                        options={[
                          { label: "Sim", value: true },
                          { label: "Não", value: false }
                        ]}
                        value={field.value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>

              {serviceCallState && (
                <div className={"d-flex justify-content-around"}>
                  <div className={"col-md-4 d-flex flex-column"}>
                    <Input
                      name={"costumer_name"}
                      placeholder={
                        serviceCallState?.company?.contact_name || "Nome"
                      }
                      label={"Nome do cliente"}
                      register={register}
                    />
                  </div>
                  <div className={"col-md-6 d-flex flex-column"}>
                    <Input
                      placeholder={serviceCallState?.company?.email || "E-mail"}
                      name={"costumer_email"}
                      type="email"
                      label={"E-mail do cliente"}
                      register={register}
                    />
                  </div>
                </div>
              )}
              {/* Formulário de Medição de Bateria */}
              {currentCorrective && (
                <div className="row mt-4">
                  <div className="col-12">
                    <h5>
                      Medição de Bateria
                      {batteries.length > 1 &&
                        ` (${batteries.length} baterias)`}
                    </h5>
                  </div>
                  {batteries.map((bat, batIdx) => (
                    <div
                      key={batIdx}
                      className="col-12 border rounded p-3 mb-3"
                    >
                      <h6 className="mb-3">
                        {batteries.length > 1
                          ? `Bateria ${batIdx + 1}`
                          : "Dados da bateria"}
                      </h6>
                      <div className="row">
                        <div className="col-md-2 mb-2">
                          <label className="form-label">PAT</label>
                          <input
                            type="text"
                            className="form-control"
                            value={bat.brand}
                            onChange={(e) =>
                              setBatteries((prev) => {
                                const u = [...prev];
                                u[batIdx] = {
                                  ...u[batIdx],
                                  brand: e.target.value
                                };
                                return u;
                              })
                            }
                          />
                        </div>
                        <div className="col-md-2 mb-2">
                          <label className="form-label">
                            Tensão total (V)
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={bat.totalVoltage}
                            onChange={(e) =>
                              setBatteries((prev) => {
                                const u = [...prev];
                                u[batIdx] = {
                                  ...u[batIdx],
                                  totalVoltage: e.target.value
                                };
                                return u;
                              })
                            }
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="row mt-2">
                        {bat.elements
                          .slice(0, bat.elementCount)
                          .map((el, idx) => (
                            <div
                              key={idx}
                              className="col-md-2 mb-2"
                            >
                              <div className="border rounded p-2">
                                <div className="fw-bold small mb-1">
                                  Elem. {idx + 1}
                                </div>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control mb-1 form-control-sm"
                                  placeholder="V"
                                  value={el.voltage}
                                  onChange={(e) =>
                                    setBatteries((prev) => {
                                      const u = [...prev];
                                      u[batIdx].elements = [
                                        ...u[batIdx].elements
                                      ];
                                      u[batIdx].elements[idx] = {
                                        ...u[batIdx].elements[idx],
                                        voltage: e.target.value
                                      };
                                      return u;
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  step="0.001"
                                  className="form-control form-control-sm"
                                  placeholder="0.000"
                                  value={el.density}
                                  onChange={(e) =>
                                    setBatteries((prev) => {
                                      const u = [...prev];
                                      u[batIdx].elements = [
                                        ...u[batIdx].elements
                                      ];
                                      u[batIdx].elements[idx] = {
                                        ...u[batIdx].elements[idx],
                                        density: e.target.value
                                      };
                                      return u;
                                    })
                                  }
                                  onBlur={(e) => {
                                    const formatted = formatDensity(
                                      e.target.value
                                    );
                                    if (
                                      formatted !== "" &&
                                      formatted !== e.target.value
                                    ) {
                                      setBatteries((prev) => {
                                        const u = [...prev];
                                        u[batIdx] = { ...u[batIdx] };
                                        u[batIdx].elements = [
                                          ...u[batIdx].elements
                                        ];
                                        u[batIdx].elements[idx] = {
                                          ...u[batIdx].elements[idx],
                                          density: formatted
                                        };
                                        return u;
                                      });
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!currentCorrective && (
                <>
                  <div className="row mt-4 mb-2">
                    <div className="col-12">
                      <h5>Medição de Bateria</h5>
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label">Marca da bateria *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={batteryBrand}
                        onChange={(e) => setBatteryBrand(e.target.value)}
                      />
                      {batteryErrors.brand && (
                        <span className="text-danger small">
                          {batteryErrors.brand}
                        </span>
                      )}
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label">Data de fabricação *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={batteryManufactureDate}
                        onChange={(e) =>
                          setBatteryManufactureDate(e.target.value)
                        }
                      />
                      {batteryErrors.manufactureDate && (
                        <span className="text-danger small">
                          {batteryErrors.manufactureDate}
                        </span>
                      )}
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label">Data da medição *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={batteryReadingDate}
                        onChange={(e) => setBatteryReadingDate(e.target.value)}
                      />
                      {batteryErrors.readingDate && (
                        <span className="text-danger small">
                          {batteryErrors.readingDate}
                        </span>
                      )}
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label">Tensão total (V) *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={batteryTotalVoltage}
                        onChange={(e) => setBatteryTotalVoltage(e.target.value)}
                        step="0.01"
                        min="0"
                      />
                      {batteryErrors.totalVoltage && (
                        <span className="text-danger small">
                          {batteryErrors.totalVoltage}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6>Elementos da bateria</h6>
                      <div className="row">
                        {batteryElements.map((el, idx) => (
                          <div key={idx} className="col-md-2 mb-2">
                            <div className="border rounded p-2">
                              <div className="fw-bold mb-1">
                                Elemento {idx + 1}
                              </div>
                              <label className="form-label small">
                                Tensão (V)
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="3"
                                step="0.01"
                                className="form-control mb-1"
                                value={el.voltage}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setBatteryElements((prev) => {
                                    const updated = [...prev];
                                    updated[idx] = {
                                      ...updated[idx],
                                      voltage: value
                                    };
                                    return updated;
                                  });
                                }}
                              />
                              <label className="form-label small">
                                Densidade
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="1.5"
                                step="0.01"
                                className="form-control"
                                value={el.density}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setBatteryElements((prev) => {
                                    const updated = [...prev];
                                    updated[idx] = {
                                      ...updated[idx],
                                      density: value
                                    };
                                    return updated;
                                  });
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </Fade>
      </div>
    );
  };

  return (
    <>
      <Dialog
        visible={visible}
        style={{ marginTop: 60, width: "80rem" }}
        className="p-fluid"
        modal
        onHide={hideForm}
        header={"Editar corretiva"}
        footer={footer}
      >
        <div className="container pt-2"> {createForm()} </div>
      </Dialog>
    </>
  );
}
